import { AfterViewInit, Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { UserService } from '../../../services/user.service';
import { User } from '../../../models/user';
import { CreditsService } from '../../../services/credits.service';
import { CreditNationType, Credits, Type } from '../../../models/credits';
import { debounceTime, filter, switchMap, tap } from 'rxjs/operators';
import { Observable, Subscription, timer } from 'rxjs';
import { DOCUMENT } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { IframeScrollService } from '../../../services/iframe-scroll.service';
import { DomainService } from '../../../services/domain.service';
import { Domain } from '../../../models/domain';
import { creditsTypeIcon } from '../../../utils/constants';
import { MenuService } from '../../../services/menu.service';
import {
  BrandName,
  formatCurrency,
  formatIntNumber,
  getCurrencySymbol,
  getDecimalSeparator,
  getNation3FlagUrl,
  getSubscriptionStatus
} from '../../../utils/utils';
import { SmsService } from '../../../services/sms.service';
import { SessionStorageService } from 'ngx-webstorage';
import { ROLE_ADMIN_ID, ROLE_OWNER_ID } from '../../../models/role';
import { LoginService } from 'src/app/core/login/login.service';

export class CreditTypeData {
  enabled: boolean;
  credits: Type | CreditNationType;
  icon: string;
  title?: Observable<string>;
  isNation: boolean;
}

@Component({
  selector: 'wsb-ui-app-header',
  templateUrl: './app-header.component.html',
  styleUrls: ['./app-header.component.scss'],
  animations: [
    trigger('EnterLeave', [
      state(
        'flyIn',
        style({
          opacity: '1'
        })
      ),
      transition(':enter', [
        style({
          opacity: 0
        }),
        animate('0.5s 300ms ease-in')
      ]),
      transition(':leave', [
        animate(
          '0.3s ease-out',
          style({
            opacity: 0
          })
        )
      ])
    ]),
    trigger('displayHeader', [
      state('loading', style({ opacity: '0' })),
      state('loaded', style({ opacity: '1' })),
      transition('loading => loaded', [animate('300ms ease-in')])
    ])
  ],
  standalone: false
})
export class AppHeaderComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('navbarHeader') $navbarHeader;
  @ViewChild('creditsNavbar') $creditsNavbar;

  private userSubscription: Subscription;
  private userLogoutSubscription: Subscription;
  menuEntries = {};

  scrollDirection = null;
  locale = 'it-IT';
  user: User | null;
  credits?: Credits;
  messageTypes = null;
  isCreditsPreferencesInitialized = false;
  creditsUpdateInProgress = false;
  creditsList: CreditTypeData[] = [];

  private scrollSubscription$: Subscription;

  hasSupportGuideUrl = false;
  hasUserManagementUrl = false;
  hasBillingUrl = false;
  domain: Domain | null;
  domainSubscription: Subscription;
  private menuEntriesSubscription: Subscription;

  decimalSeparator;
  currency;
  formatIntNumber;
  allowProfileManageAndShopAndBilling: any;

  headerState = 'loading';
  subs;
  openSubs;
  expiring = false;
  expired = false;
  opened = false;
  constructor(
    private loginService: LoginService,
    private userService: UserService,
    private creditsService: CreditsService,
    private translateService: TranslateService,
    private router: Router,
    @Inject(DOCUMENT) private document: Document,
    private iframeScrollService: IframeScrollService,
    private domainService: DomainService,
    private menuService: MenuService,
    private smsService: SmsService,
    private sessionStorage: SessionStorageService
  ) {}

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.headerState = 'loaded';
    }, 1100);
  }

  ngOnInit() {
    this.reloadComponent();
    this.domainSubscription = this.domainService.getDomainState().subscribe(x => (this.domain = x));
    this.handleScroll();

    this.userSubscription = timer(0, 30000)
      .pipe(
        switchMap(() => this.userService.getAuthenticationState()),
        tap((loggedUser: User) => {
          this.user = loggedUser;
          this.allowProfileManageAndShopAndBilling =
            this.user?.profile.role.id === ROLE_OWNER_ID || this.user?.profile.role.id === ROLE_ADMIN_ID;
          this.currency = getCurrencySymbol();
          this.checkIfLayoutImpersonate();
          // this.isCreditsPreferencesInitialized = false;
        }),
        filter(loggedUser => loggedUser !== null),
        tap(() => {
          this.creditsUpdateInProgress = true;

          this.smsService.getMessageTypes().subscribe(x => {
            const messageTypes = [];
            x.data.forEach(y => {
              messageTypes[y.id] = y.label;
            });
            this.messageTypes = messageTypes;
            if (this.credits) {
              if (!this.isCreditsPreferencesInitialized) {
                this.initializeCredits();
              }
            }
          });
        }),
        switchMap(() => this.creditsService.getUserCredits())
      )
      .subscribe(credits => {
        credits.subscriptions.forEach(x => {
          if (!x.details.recurringSubscription) {
            const status = getSubscriptionStatus(x.details.subscriptionExpiryTime);
            if (status === 'expiring') {
              this.expiring = true;
              this.openSubs = true;
            } else if (status === 'expired') {
              this.expired = true;
              this.openSubs = true;
            }
          }
        });

        this.credits = credits;
        if (this.messageTypes) {
          if (!this.isCreditsPreferencesInitialized) {
            this.initializeCredits();
          } else {
            this.processCredits();
          }
        }
      });

    this.menuEntriesSubscription = this.menuService.getMenuEntriesState().subscribe(entries => (this.menuEntries = entries));

    this.userLogoutSubscription = this.userService
      .getAuthenticationState()
      .pipe(filter(loggedUser => !loggedUser))
      .subscribe(() => {
        this.creditsList = null;
        this.creditsList = [];
        this.isCreditsPreferencesInitialized = false;
      });

    this.translateService.onLangChange
      .pipe(switchMap(() => this.translateService.get('support_guide_url')))
      .subscribe(label => (this.hasSupportGuideUrl = label && label !== '' && label !== 'support_guide_url'));

    this.translateService.onLangChange
      .pipe(switchMap(() => this.translateService.get('topmenu_user_management')))
      .subscribe(label => (this.hasUserManagementUrl = label && label !== '' && label !== 'topmenu_user_management'));

    this.translateService.onLangChange
      .pipe(switchMap(() => this.translateService.get('topmenu_billing')))
      .subscribe(label => (this.hasBillingUrl = label && label !== '' && label !== 'topmenu_billing'));

    this.decimalSeparator = getDecimalSeparator();
    this.currency = getCurrencySymbol();
    this.formatIntNumber = formatIntNumber;
  }

  /**
   * Subscribe to login service to reload creditsList if impersonate
   */
  reloadComponent() {
    this.loginService.reloadHeader.subscribe(value => {
      if (value) {
        this.creditsService.getUserCredits().subscribe(credits => {
          this.credits = credits;
          this.initializeCredits();
          // update subscrption header
          this.userService.loadHeaderSubscription(credits.subscriptions);
        });
      }
    });
  }

  isCreditTypeChecked(credit): number {
    if (this.user && this.user.preferences.displayCredits) {
      return this.user.preferences.displayCredits.findIndex(creditType => creditType === credit.type);
    }
    return -1;
  }

  //
  // getBrowserNumberFormat(): any {
  //   return {
  //     thousandsSeparator: Number(10000)
  //       .toLocaleString()
  //       .charAt(2),
  //     decimalSeparator: Number(1.1)
  //       .toLocaleString(this.user.preferences.language.substr(0, 2))
  //       .charAt(1)
  //   };
  // }

  get creditFormatted() {
    if (this.credits !== undefined && this.credits.money !== null) {
      return formatCurrency(this.credits.money);
      // return this.credits.money.toLocaleString(this.user.preferences.language.substr(0, 2), {
      //   minimumFractionDigits: 2
      // });
    }
    return null;
  }

  get creditFormattedDigits() {
    if (this.creditFormatted) {
      return this.creditFormatted.split(getDecimalSeparator())[0];
    }

    return null;
  }

  get creditFormattedFractions() {
    if (this.creditFormatted) {
      return this.creditFormatted.split(getDecimalSeparator())[1];
    }

    return null;
  }

  get userLogin() {
    if (this.user) {
      return this.user.login;
    }
    return null;
  }

  get userRegistrationEmail() {
    if (this.user) {
      return this.user.profile.registrationEmail;
    }
    return null;
  }

  get userFullName() {
    if (this.user) {
      return this.user.profile.name + ' ' + this.user.profile.surname;
    }
    return null;
  }

  get userEmail() {
    if (this.user) {
      return this.user.profile.email;
    }
    return null;
  }

  get companyName() {
    if (this.user) {
      return this.user.account.companyName;
    }
    return null;
  }

  private processCredits() {
    if (this.credits.types && this.creditsList) {
      this.credits.types.forEach(creditType => {
        const creditTypeData = this.creditsList.find(credit => credit.credits.type === creditType.type);
        if (creditTypeData) {
          creditTypeData.credits.quantity = creditType.quantity;
        }
      });
    }
    this.creditsUpdateInProgress = false;
  }

  private initializeCredits() {
    const creditsList = [];
    if (this.credits.types) {
      this.credits.types.map(creditType => {
        const showCreditTypeIndex = this.isCreditTypeChecked(creditType);
        const iconType = creditsTypeIcon[creditType.type];
        creditsList.push({
          credits: creditType,
          enabled: showCreditTypeIndex !== -1,
          icon: iconType,
          // title: this.translateService.get('atlas_credit_type_' + creditType),
          title: typeof this.messageTypes[creditType.type] !== 'undefined' ? this.messageTypes[creditType.type] : null,
          order: showCreditTypeIndex,
          isNation: false
        });
      });
    }
    // Add nation credits
    if (this.credits.nations) {
      this.credits.nations.map(creditNationType => {
        creditNationType.type = creditNationType.nation;
        const showCreditTypeIndex = this.isCreditTypeChecked(creditNationType);
        creditsList.push({
          credits: creditNationType,
          enabled: showCreditTypeIndex !== -1,
          icon: getNation3FlagUrl(creditNationType.nation),
          // title: this.translateService.get('nation_' + creditNationType.nation),
          title: creditNationType.nation.toUpperCase(),
          order: showCreditTypeIndex,
          isNation: true
        });
      });
    }
    // this.creditsList = creditsList;
    // this.creditsList = creditsList.filter(x => x.enabled).sort((a, b) => (a.order > b.order) ? 1 : -1);
    this.creditsList = creditsList.sort((a, b) => (a.order === -1 || (b.order !== -1 && a.order > b.order) ? 1 : -1));
    this.isCreditsPreferencesInitialized = true;
    this.creditsUpdateInProgress = false;
  }

  handleScroll() {
    /* php-integration-block */
    this.scrollSubscription$ = this.iframeScrollService.$scrollEvent.pipe(debounceTime(150)).subscribe(scroll => {
      const body = this.document.body;
      const header = this.$navbarHeader.nativeElement;
      const currentScroll = window.scrollY;
      const topBar = this.document.getElementById('topbar-layout');

      if (scroll.scrollY < currentScroll) {
        this.scrollDirection = 'up';
      } else {
        this.scrollDirection = 'down';
      }

      if (scroll.scrollY > 50 && this.scrollDirection === 'down') {
        topBar.classList.add('is--slim');
        header.classList.add('is--slim');
        body.classList.add('has--header-slim');
      } else {
        topBar.classList.remove('is--slim');
        header.classList.remove('is--slim');
        body.classList.remove('has--header-slim');
      }
    });
  }

  toogleMobileNav() {
    const body = this.document.body;

    if (body.classList.contains('has--nav-visible')) {
      body.classList.remove('has--nav-visible');
    } else {
      body.classList.add('has--nav-visible');
    }
  }

  checkIfLayoutImpersonate() {
    const topBar = this.document.getElementById('topbar-layout');
    if (this.isImpersonatedSubuser() || this.isImpersonatedIgo()) {
      topBar.classList.add('layout-impersonate');
      document.documentElement.style.setProperty('--header-height-impersonate', '3.5rem');
    } else {
      document.documentElement.style.setProperty('--header-height-impersonate', '0rem');
      if (topBar.classList.contains('layout-impersonate')) {
        topBar.classList.remove('layout-impersonate');
      }
    }
  }

  logout() {
    this.router.navigate(['logout']);
  }

  isImpersonatedSubuser() {
    return this.userService.isImpersonatingSubuser();
  }

  isImpersonatedIgo() {
    return this.userService.isImpersonatingIGo();
  }

  deimpersonate() {
    this.router.navigate(['deimpersonate']);
  }

  openGuide() {
    window.open(this.translateService.instant('support_guide_url'), '_blank');
  }

  dropdownOpend() {
    this.opened = true;
  }

  displayHelpButton() {
    return this.menuEntries['support'] || this.sessionStorage.retrieve('wsb-brand-name') === BrandName.VODAFONE;
  }

  ngOnDestroy(): void {
    this.userSubscription.unsubscribe();
    this.scrollSubscription$.unsubscribe();
    this.userLogoutSubscription.unsubscribe();
    this.domainSubscription.unsubscribe();
    this.menuEntriesSubscription.unsubscribe();
  }
}
